import React, { useEffect, useState } from "react";
import AddCategoryModal from "./Modal/AddCategoryModal";
import EditCategoryModal from "./Modal/EditCategoryModal";
import RecipeSearchDevOps from "./components/RecipeSearchDevOps";
import ReOrderCategory from "./Modal/ReOrderCategory";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Skeleton,
  CircularProgress,
} from "@mui/material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  Chip,
} from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";

import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

function Category() {
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  //
  const [loading, setLoading] = useState(true); // first load
  const [refreshing, setRefreshing] = useState(false); // add/edit refresh

  const { t, i18n } = useTranslation(["Recettes"]);
  const userState = useSelector((state) => state.user);
  const navigate = useNavigate();
  const [openReorder, setOpenReorder] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [selectedCat, setSelectedCat] = useState(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [filter, setFilter] = useState("ALL");
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "success", // success | error | warning | info
  });
  // ================= Roles =================
  const ADMIN_ROLES = ["RecipeAdmin", "AdminTI"];
  //const ADMIN_ROLES = ["ReportAdmin"]; //pour tester ;
  const hasAdminAccess = (roles = []) =>
    roles.some((role) => ADMIN_ROLES.includes(role));
  const canAdmin = hasAdminAccess(userState?.roles || []);
  const handleNoPermission = () => {
    showSnack(t("noPermission"), "warning");
  };
  // ================= function helper =================
  const showSnack = (message, severity = "info") => {
    setSnack({
      open: true,
      message,
      severity,
    });
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // ================= FETCH =================
  const fetchCategories = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/CategoryRecipe/GetRecipeCategories`,
        {
          headers: {
            Authorization: `Bearer ${userState.token}`,
          },
        },
      );

      const data = await res.json();
      setCategories(data);
    } catch (error) {
      console.error(error);
    }

    if (isRefresh) setRefreshing(false);
    else setLoading(false);
  };

  // ================= TOGGLE =================
  const toggleActive = async (cat) => {
    try {
      const newStatus = !cat.estActif;

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/CategoryRecipe/ToggleRecipeCategory/${cat.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userState.token}`,
          },
          body: JSON.stringify({
            estActif: newStatus,
          }),
        },
      );

      const data = await response.json();

      if (!response.ok) {
        showSnack(
          userState.lang === "FR" ? data.messageFr : data.messageEn,
          "error",
        );
        return;
      }

      // update UI
      setCategories((prev) =>
        prev.map((c) => (c.id === cat.id ? { ...c, estActif: newStatus } : c)),
      );

      showSnack(
        userState.lang === "FR" ? data.messageFr : data.messageEn,
        "success",
      );
    } catch (err) {
      console.error(err);
      showSnack("Error", "error");
    }
  };

  // ================= DELETE =================
  const handleDelete = async () => {
    if (!categoryToDelete) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/CategoryRecipe/Deletecategory/${categoryToDelete.id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${userState.token}`,
          },
        },
      );

      const data = await response.json();

      if (!response.ok) {
        showSnack(
          userState.lang === "FR" ? data.messageFr : data.messageEn,
          "error",
        );
        return;
      }
      setCategories((prev) => prev.filter((c) => c.id !== categoryToDelete.id));
    } catch (err) {
      console.error(err);
    } finally {
      setDeleteDialogOpen(false);
      setCategoryToDelete(null);
    }
  };

  // ================= ADD =================
  const handleSaveAdd = async (form) => {
    const formData = new FormData();
    formData.append("textFr", form.textFr);
    formData.append("textEn", form.textEn);
    formData.append("estActif", form.estActif);
    if (form.image) formData.append("image", form.image);

    await fetch(
      `${process.env.REACT_APP_API_URL}/CategoryRecipe/CreateRecipeCategory`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${userState.token}`,
        },
        body: formData,
      },
    );

    setOpenAdd(false);
    fetchCategories(true); // ✅ refresh propre
  };

  // ================= EDIT =================
  const handleSaveEdit = async (form) => {
    const formData = new FormData();
    formData.append("textFr", form.textFr);
    formData.append("textEn", form.textEn);
    formData.append("estActif", form.estActif);

    if (form.image) {
      formData.append("image", form.image);
    }

    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/CategoryRecipe/UpdateRecipeCategory/${selectedCat.id}`,
      {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${userState.token}`,
        },
        body: formData,
      },
    );

    if (!response.ok) return;

    setOpenEdit(false);
    fetchCategories(true); // ✅ refresh propre
  };

  // ================= LABEL =================
  const getLabel = (cat) => (userState.lang === "FR" ? cat.textFr : cat.textEn);

  const filtered = categories
    // NON ADMIN → seulement actifs
    .filter((c) => {
      if (canAdmin) return true;
      return c.estActif;
    })

    // SEARCH
    .filter((c) => getLabel(c).toLowerCase().includes(search.toLowerCase()))

    // FILTER admin
    .filter((c) => {
      if (!canAdmin) return true;

      if (filter === "ALL") return true;
      if (filter === "ACTIVE") return c.estActif;
      if (filter === "INACTIVE") return !c.estActif;

      return true;
    });

  return (
    <Box sx={{ p: 3, background: "#f4f6f8", minHeight: "100vh" }}>
      {/* OVERLAY LOADING (NO FLASH) */}
      {refreshing && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(255,255,255,0.5)",
            backdropFilter: "blur(2px)",
            zIndex: 999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {/* HEADER */}
      {/* FILTER BAR */}
      {canAdmin && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 1,
            mb: 3,
          }}
        >
          {/* ALL */}
          <Chip
            icon={<FilterListIcon />}
            label={userState.lang === "FR" ? "Tous" : "All"}
            clickable
            onClick={() => setFilter("ALL")}
            color={filter === "ALL" ? "primary" : "default"}
            variant={filter === "ALL" ? "filled" : "outlined"}
            sx={{ fontWeight: 600 }}
          />

          {/* ACTIVE */}
          <Chip
            icon={<CheckCircleIcon />}
            label={userState.lang === "FR" ? "Actifs" : "Active"}
            clickable
            onClick={() => setFilter("ACTIVE")}
            color={filter === "ACTIVE" ? "success" : "default"}
            variant={filter === "ACTIVE" ? "filled" : "outlined"}
            sx={{
              fontWeight: 600,
              ...(filter === "ACTIVE" && {
                backgroundColor: "#4caf50",
                color: "white",
                "& .MuiChip-icon": {
                  color: "white",
                },
              }),
            }}
          />

          {/* INACTIVE */}
          <Chip
            icon={<CancelIcon />}
            label={userState.lang === "FR" ? "Inactifs" : "Inactive"}
            clickable
            onClick={() => setFilter("INACTIVE")}
            color={filter === "INACTIVE" ? "error" : "default"}
            variant={filter === "INACTIVE" ? "filled" : "outlined"}
            sx={{ fontWeight: 600 }}
          />
        </Box>
      )}
      {/* HEADER PRO STYLE SAAS */}
      <Box
        sx={{
          mb: 3,
          p: 2,
          borderRadius: 4,
          background: "white",
          boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
        }}
      >
        {/* TOP ROW */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Typography fontSize={20} fontWeight={700}>
            Categories
          </Typography>

          {canAdmin && (
            <Box sx={{ display: "flex", gap: 1 }}>
              {/* REORDER BUTTON */}
              <Button
                startIcon={<DragIndicatorIcon />}
                variant="contained"
                onClick={() => setOpenReorder(true)}
                sx={{
                  borderRadius: 3,
                  textTransform: "none",
                  fontWeight: 600,
                  px: 2.5,
                  height: 42,
                  background: "linear-gradient(135deg,#1976d2,#42a5f5)",
                  boxShadow: "0 4px 14px rgba(25,118,210,0.25)",

                  "&:hover": {
                    background: "linear-gradient(135deg,#1565c0,#1e88e5)",
                  },
                }}
              >
                {t("btnReorder")}
              </Button>

              {/* ADD BUTTON */}
              <Button
                startIcon={<AddIcon />}
                variant="contained"
                onClick={() => setOpenAdd(true)}
                sx={{
                  borderRadius: 3,
                  textTransform: "none",
                  fontWeight: 600,
                  px: 2.5,
                  height: 42,
                  background: "linear-gradient(135deg,#1976d2,#42a5f5)",
                  boxShadow: "0 4px 14px rgba(25,118,210,0.25)",

                  "&:hover": {
                    background: "linear-gradient(135deg,#1565c0,#1e88e5)",
                  },
                }}
              >
                {t("add")}
              </Button>
            </Box>
          )}
        </Box>

        {/* SEARCH ROW */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 2,
            alignItems: "center",
          }}
        >
          {/* CATEGORY SEARCH */}
          <TextField
            size="small"
            placeholder={
              userState.lang === "FR"
                ? "Rechercher une catégorie..."
                : "Search category..."
            }
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              background: "#f8f9fb",
              borderRadius: 2,
              width: "100%",
              "& .MuiInputBase-root": {
                height: 40, // 👈 même taille partout
              },
            }}
            InputProps={{
              startAdornment: (
                <SearchIcon fontSize="small" sx={{ mr: 1, color: "#999" }} />
              ),
              endAdornment: search ? (
                <IconButton size="small" onClick={() => setSearch("")}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              ) : null,
            }}
          />

          {/* RECIPE SEARCH */}
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <RecipeSearchDevOps token={userState.token} lang={userState.lang} />
          </Box>
        </Box>
      </Box>

      {/* GRID */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(180px,1fr))",
          gap: 3,
        }}
      >
        {loading
          ? Array.from(new Array(8)).map((_, i) => (
              <Skeleton
                key={i}
                variant="rounded"
                height={140}
                sx={{ borderRadius: 3 }}
              />
            ))
          : filtered.map((cat) => (
              <Box
                key={cat.id}
                onClick={() => navigate(`/Category/${cat.id}`)}
                sx={{
                  position: "relative",
                  borderRadius: 4,
                  overflow: "hidden",
                  background: "white",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  cursor: "pointer",
                  transition: "0.3s",
                  "&:hover": {
                    transform: "translateY(-5px)",
                    boxShadow: "0 8px 20px rgba(0,0,0,0.15)",
                  },
                  "&:hover .actions": {
                    opacity: 1,
                  },
                }}
              >
                {/* IMAGE */}
                <Box
                  sx={{
                    height: 90,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "#fafafa",
                  }}
                >
                  <img
                    src={cat.imageUrl}
                    alt=""
                    style={{
                      width: 60,
                      height: 60,
                      objectFit: "contain",
                    }}
                  />
                </Box>

                {/* LABEL */}
                <Box sx={{ p: 1 }}>
                  <Typography fontSize={13} fontWeight={500} textAlign="center">
                    {getLabel(cat)}
                  </Typography>
                </Box>
                <Box
                  sx={{ display: "flex", justifyContent: "center", mt: 0.5 }}
                >
                  <Chip
                    size="small"
                    label={
                      cat.estActif
                        ? userState.lang === "FR"
                          ? "Actif"
                          : "Active"
                        : userState.lang === "FR"
                          ? "Inactif"
                          : "Inactive"
                    }
                    sx={{
                      fontSize: 11,
                      fontWeight: 600,
                      height: 20,
                      color: "white",
                      backgroundColor: cat.estActif ? "#4caf50" : "#9e9e9e",
                    }}
                  />
                </Box>

                {/* ACTIONS */}
                {canAdmin && (
                  <Box
                    className="actions"
                    sx={{
                      position: "absolute",
                      top: 5,
                      right: 5,
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                      opacity: 0,
                      transition: "0.3s",
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!canAdmin) {
                          handleNoPermission();
                          return;
                        }
                        toggleActive(cat);
                      }}
                      sx={{
                        background: "white",
                        color: cat.estActif ? "#4caf50" : "#9e9e9e",
                      }}
                    >
                      {cat.estActif ? (
                        <VisibilityIcon fontSize="small" />
                      ) : (
                        <VisibilityOffIcon fontSize="small" />
                      )}
                    </IconButton>

                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!canAdmin) {
                          handleNoPermission();
                          return;
                        }
                        setSelectedCat(cat);
                        setOpenEdit(true);
                      }}
                      sx={{ background: "white" }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>

                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!canAdmin) {
                          handleNoPermission();
                          return;
                        }
                        setCategoryToDelete(cat);
                        setDeleteDialogOpen(true);
                      }}
                      sx={{ background: "white" }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Box>
            ))}
      </Box>

      {/* MODALS */}
      <AddCategoryModal
        open={openAdd}
        onClose={() => setOpenAdd(false)}
        onSave={handleSaveAdd}
      />

      <EditCategoryModal
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        onSave={handleSaveEdit}
        category={selectedCat}
      />
      {/* DELETE CONFIRM DIALOG */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 5,
            width: 420,
            p: 3,
            textAlign: "center",
            background: "#fff",
          },
        }}
      >
        {/* ICON */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
          <WarningAmberRoundedIcon
            sx={{
              fontSize: 60,
              color: "#ff9800",
              background: "#fff3e0",
              borderRadius: "50%",
              p: 1.5,
            }}
          />
        </Box>

        {/* TITLE */}
        <DialogTitle sx={{ fontWeight: 800, fontSize: 20 }}>
          {userState.lang === "FR"
            ? "Supprimer cette catégorie ?"
            : "Delete this category?"}
        </DialogTitle>

        {/* TEXT */}
        <DialogContent>
          <DialogContentText
            sx={{
              fontSize: 14,
              color: "#666",
              mt: 1,
            }}
          >
            {userState.lang === "FR"
              ? `Cette action est irréversible. La catégorie “${categoryToDelete?.textFr}” sera définitivement supprimée.`
              : `This action is irreversible. The category “${categoryToDelete?.textEn}” will be permanently deleted.`}
          </DialogContentText>
        </DialogContent>

        {/* ACTIONS */}
        <DialogActions
          sx={{
            justifyContent: "center",
            gap: 2,
            mt: 2,
          }}
        >
          {/* CANCEL */}
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            variant="outlined"
            sx={{
              borderRadius: 3,
              px: 3,
              textTransform: "none",
              borderColor: "#ccc",
              color: "#555",
              "&:hover": {
                borderColor: "#999",
                background: "#f5f5f5",
              },
            }}
          >
            {userState.lang === "FR" ? "Annuler" : "Cancel"}
          </Button>

          {/* DELETE */}
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            sx={{
              borderRadius: 3,
              px: 3,
              textTransform: "none",
              fontWeight: 600,
              boxShadow: "0 6px 16px rgba(255,0,0,0.25)",
              "&:hover": {
                boxShadow: "0 8px 20px rgba(255,0,0,0.35)",
              },
            }}
          >
            {userState.lang === "FR" ? "Supprimer" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
      <ReOrderCategory
        open={openReorder}
        onClose={() => setOpenReorder(false)}
        categories={categories}
        refresh={fetchCategories}
      />
      {/* Alert Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
          severity={snack.severity}
          variant="filled"
          sx={{
            width: "100%",
            borderRadius: 2,
          }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
export default Category;
